/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Divider, Layout, Tooltip } from 'antd';
import {
  ContentShare,
  LocalVideo,
  useContentShareControls,
  useContentShareState,
  useLocalVideo,
  useRemoteVideoTileState,
  useRosterState,
  useToggleLocalMute,
} from 'amazon-chime-sdk-component-library-react';
import VideoCallSettings from './VideoCallSetings';
import InviteParticipants from './InviteParticipants';
import UserVideoTile from './UserVideoTile';
import maximizeIconUrl from './maximize.svg?img';
import minimizeIconUrl from './minimize.svg?img';
import CustomSwitch from '../Common/CustomSwitch';
import {
  decodeExternalUserId,
  getName,
  objectSort,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  CallEndStatus,
  CallFailStatus,
  CallWaitStatus,
  DateTimeFormat,
  PhotoResolution,
  UnknownUsername,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  CallIcon,
  CallNewIcon,
  CloseIcon,
  ContactIcon,
  ContactIcon1,
  ContactImage,
  EndCallIcon,
  MicOffIcon,
  MicOnIcon,
  NewCloseIcon,
  ScheduleIcon,
  ScreenShareOnIcon,
  SettingsIcon,
  VideoOffIcon,
  VideoOnIcon,
  VisibilityIcon,
  VisibilityOffIcon,
  ZoomIcon,
} from '../Common/Icons';
import { getMeetingInfo } from '@vantiva-sa/homesight_humaui/Store/videoCallStore';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getUserHubs } from '@vantiva-sa/homesight_humaui/Store/dataStore';

const getGridLayout = (count) => {
  if (count <= 2) return [count === 2 ? 2 : 1];
  if (count === 3) return [2, 1];
  if (count === 4) return [2, 2];
  if (count === 5) return [3, 2];
  if (count === 6) return [3, 3];
  if (count === 7) return [3, 3, 1];
  if (count === 8) return [3, 3, 2];
  return [3, 3, 3];
};

/* return number of rows and columns for given number of users */
const getGridRowCols = (count) => {
  if (!count) return [1, 1];
  if (count === 0 || count === 1) return [1, 1];
  if (count === 2) return [1, 2];
  if (count === 3 || count === 4) return [2, 2];
  if (count === 5 || count === 6) return [2, 3];
  if (count >= 7) return [3, 3];
  return [1, 1];
};

const getDisplayName = (user, name) => {
  let username = getName(user);
  return username !== UnknownUsername ? username : name;
};

const formatDuration = (start, end) => {
  let hours = end.diff(start, 'hours');
  start.add(hours, 'hours');
  let minutes = end.diff(start, 'minutes');
  start.add(minutes, 'minutes');
  let seconds = end.diff(start, 'seconds');
  // if (minutes < 10) minutes = "0" + minutes;
  if (hours > 0) {
    // if (hours < 10) hours = "0" + hours;
    return hours + ' hr ' + minutes + ' min';
  }
  return minutes < 1 ? seconds + ' sec' : minutes + ' min ';
};

const formatMiniDuration = (elapsedSeconds) => {
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const VideoCallComponent = ({
  isSetupDone,
  callStatus,
  onEndCall,
  meetingInfo,
  participantsMuteStatus,
  videoCallProp = true,
  customBackgroundImage,
  isMini = false,
  onMinimize,
  onExpand,
}) => {
  const ref = useRef();
  const videoTilesContainerRef = useRef(null);
  const settingsActionRef = useRef(null);
  const optionsSiderRef = useRef(null);
  const trans = useSelector(translator);

  const CallOptions = {
    invite: trans('PARTICIPANTS'),
    settings: trans('SETTINGS'),
  };
  const { startTime, endTime } = meetingInfo ?? {};
  const currentUser = useSelector(getCurrentUser);
  const eventInfo = useSelector(getMeetingInfo)?.eventInfo;
  const allUserInfo = useSelector((state) => state.data.userInfo);
  const loggedInUser = allUserInfo?.[currentUser?.useruuid];
  const shouldShowProviderBackground = loggedInUser?.usertype === 'Staff';

  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { muted, toggleMute } = useToggleLocalMute();
  const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
  const { toggleContentShare } = useContentShareControls();
  const [isBlurEnabled, setIsBlurEnabled] = useState(false);
  const [isReplacementEnabled, setIsReplacementEnabled] = useState(false);
  const [selectedCustomBackground, setSelectedCustomBackground] = useState(null);
  const lastEffectRef = useRef('none');
  const [effectHandlers, setEffectHandlers] = useState({});
  const [isApplyingEffect, setIsApplyingEffect] = useState(false);

  const { roster } = useRosterState();
  const { attendeeIdToTileId } = useRemoteVideoTileState();

  const isEventCall = !!eventInfo;
  const isPresenting = !!sharingAttendeeId;
  const attendees = useMemo(() => Object.values(roster), [roster]);
  const isAnyoneJoined = useMemo(() => attendees.length > 1, [attendees]);
  const [localChimeUser, attendeeList] = useMemo(() => {
    let local = {};
    let others = (attendees ?? []).reduce((result, a) => {
      const { useruuid, name } = decodeExternalUserId(a.externalUserId);
      if (useruuid === currentUser.useruuid) {
        local = { ...a, name, useruuid };
      } else {
        result[useruuid] = { ...a, name, useruuid };
      }
      return result;
    }, {});
    others = Object.values(others).sort(objectSort('name'));
    return [local, others];
  }, [attendees]);

  const callWaited = useMemo(() => CallWaitStatus.includes(callStatus), [callStatus]);
  const callFailed = useMemo(() => CallFailStatus.includes(callStatus), [callStatus]);
  const callEnded = useMemo(() => CallEndStatus.includes(callStatus), [callStatus]);

  /* local component state */
  const [containerSize, setContainerSize] = useState([]);

  /* call-options related variables */
  const [selectedOption, setSelectedOption] = useState(null);

  /* call information related */
  const [duration, setDuration] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  /* invites tab related variables */
  const invites = useSelector((state) => state.videoCall.invites);

  /* settings tab related variables */
  const [showSelfView, setShowSelfView] = useState(true);

  const [isCroppingEnabled, setIsCroppingEnabled] = useState(false);
  const [showZoomModeBar, setShowZoomModeBar] = useState(true);
  const [cropControlKey, setCropControlKey] = useState(0);
  const [settingsSiderStyle, setSettingsSiderStyle] = useState(null);
  const userHubs = useSelector(getUserHubs(currentUser?.useruuid));

  const userHubsMapper = useMemo(() => {
    return userHubs.reduce((result, hub) => {
      result[hub.useruuid] = hub;
      return result;
    }, {});
  }, [userHubs]);

  useEffect(() => {
    if (attendees.length > 2) setIsCroppingEnabled(false);
  }, [attendees]);

  const gridSize = useMemo(
    () => (callWaited ? 1 : attendees?.length - 1),
    [attendees.length, callWaited]
  );

  const handleApplyingStateChange = (isApplying) => {
    setIsApplyingEffect(isApplying);
  };
  const videoTileSize = useMemo(() => {
    if (isMini && attendeeList.length === 1) {
      return { height: '100%', width: '100%', aspectRatio: 'auto' };
    }

    const videoContainer = ref?.current;
    const containerWidth = videoContainer?.offsetWidth;
    const containerHeight = videoContainer?.offsetHeight;
    const [rows, cols] = getGridRowCols(gridSize);
    let maxWidth = (containerWidth - (cols - 1) * 10) / cols;
    let heightByWidth = (maxWidth * 9) / 16;
    let maxHeight = (containerHeight - (rows - 1) * 10) / rows;
    let height = Math.min(heightByWidth, maxHeight);
    let width = (height * 16) / 9;
    return {
      height: `${height}px`,
      width: `${width}px`,
      aspectRatio: '16 / 9',
    };
  }, [ref?.current, gridSize, containerSize, isMini, attendeeList.length]);

  const calleeName = useMemo(() => {
    let name = '';
    if (!attendeeList?.length) {
      let user = invites.filter((u) => u.useruuid !== currentUser.useruuid);
      user = (user || [])[0] || {};
      user = allUserInfo[user?.useruuid];
      name = getDisplayName(user);
    } else {
      if (attendeeList?.length) {
        let user = attendeeList[0];
        let userInfo = allUserInfo[user.useruuid];
        name = getDisplayName(userInfo, user.name);
        if (attendeeList.length > 1) {
          name += ' +' + (attendeeList.length - 1);
        }
      }
    }
    return name;
  }, [callWaited, invites, attendeeList]);

  const callFormatDuration = (start, end) => {
    const duration = moment.duration(end.diff(start));
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    const components = [];
    if (hours > 0) {
      components.push(`${hours} hr`);
    }
    if (minutes > 0 || (hours === 0 && seconds === 0)) {
      components.push(`${minutes} min`);
    }
    if (seconds > 0 && hours === 0 && minutes === 0) {
      components.push(`${seconds} sec`);
    }
    const formattedStartTime = moment(start).format('h:mma');
    const formattedEndTime = moment(end).format('h:mma');
    return `${trans('CALLWAS')} ${components.join(', ')} ${trans(
      'FROM'
    )} ${formattedStartTime}-${formattedEndTime}`;
  };

  useEffect(() => {
    const videoContainer = ref.current;
    if (!videoContainer) return undefined;

    const handleResize = () => {
      const width = videoContainer?.offsetWidth;
      const height = videoContainer?.offsetHeight;
      if (!width || !height) return;
      setContainerSize((size) =>
        size[0] === width && size[1] === height ? size : [width, height]
      );
    };

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(handleResize);
    resizeObserver?.observe(videoContainer);
    window.addEventListener('resize', handleResize);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [isMini]);

  /* To enable video by default  */
  useEffect(() => {
    if (isSetupDone && !isVideoEnabled) {
      toggleVideo();
    }
  }, [isSetupDone]);

  /* Updates duration of call */
  useEffect(() => {
    const interval = startTime
      ? setInterval(() => {
          const stime = moment(startTime, DateTimeFormat);
          setElapsedSeconds(Math.max(0, moment().diff(stime, 'seconds')));
          const dur = formatDuration(stime, moment());
          setDuration(dur);
        }, 1000)
      : null;

    return () => {
      interval && clearInterval(interval);
    };
  }, [startTime]);

  const optionsClassname = (option) => {
    return selectedOption === option ? 'selected' : '';
  };

  const getAlignedSettingsSiderStyle = () => {
    const containerEl = videoTilesContainerRef.current;
    const settingsButtonEl = settingsActionRef.current;

    if (!containerEl || !settingsButtonEl) return null;

    const containerRect = containerEl.getBoundingClientRect();
    const buttonRect = settingsButtonEl.getBoundingClientRect();
    const fallbackSiderWidth = 430;
    const siderWidth = optionsSiderRef.current?.offsetWidth || fallbackSiderWidth;

    let left = buttonRect.right - containerRect.left - siderWidth;
    const maxLeft = Math.max(0, containerRect.width - siderWidth);
    left = Math.max(0, Math.min(left, maxLeft));

    return { left: `${left}px` };
  };

  const handleOptionClick = (option) => {
    if (option === CallOptions.settings) {
      if (selectedOption === option) {
        setSelectedOption(null);
        setSettingsSiderStyle(null);
      } else {
        // Set initial anchor before render to avoid center-then-move flash.
        setSettingsSiderStyle(getAlignedSettingsSiderStyle());
        setSelectedOption(option);
      }
      return;
    }

    if (selectedOption === option) {
      setSelectedOption(null);
    } else {
      setSelectedOption(option);
    }
  };

  const renderTiles = () => {
    let layout = getGridLayout(attendeeList?.length);
    let trimList = attendeeList.slice(0, 9); /* max 9 other users */
    let formattedList = [];
    let index = 0;
    for (const count of layout) {
      formattedList.push(trimList.slice(index, index + count));
      index += count;
    }
    return formattedList.map((row, i) => (
      <div key={`grid-row-${i}`} className="video-grid-row">
        {row.map((user) => {
          const { chimeAttendeeId, useruuid, name } = user;
          const isMuted = participantsMuteStatus[chimeAttendeeId];
          const tileid = attendeeIdToTileId[chimeAttendeeId];
          const userInfo = allUserInfo[useruuid];
          let username = getDisplayName(userInfo, name);
          return tileid ? (
            <div key={username} style={videoTileSize}>
              <UserVideoTile
                tileId={tileid}
                name={username}
                isMuted={isMuted}
                showNameplate={!isMini}
                videoProps={{ className: 'remote-video' }}
                isCroppingEnabled={isCroppingEnabled}
                setShowZoomModeBar={setShowZoomModeBar}
                showZoomModeBar={showZoomModeBar}
                cropControlKey={cropControlKey}
                setIsCroppingEnabled={setIsCroppingEnabled}
                principalDeviceUUID={principalDeviceUUID}
              />
            </div>
          ) : (
            useruuid !== currentUser.useruuid && (
              <div key={username} style={videoTileSize} className="video-off-container flex-center">
                <UserVideoTile
                  name={username}
                  user={userInfo}
                  isMuted={isMuted}
                  showNameplate={!isMini}
                />
              </div>
            )
          );
        })}
      </div>
    ));
  };

  const renderSelfTile = () => {
    return (
      showSelfView && (
        <>
          {isVideoEnabled ? (
            <>
              <LocalVideo className="local-video" />
              <VisibilityOffIcon className="visibility-icon" />
              {isApplyingEffect && (
                <div className="video-effect-applying-overlay">
                  <div className="video-effect-spinner" />
                </div>
              )}
            </>
          ) : (
            <div className="video-off-container flex-center">
              <UserVideoTile
                user={allUserInfo[currentUser.useruuid]}
                scale={1.4}
                showNameplate={!isMini}
              />
              <VisibilityIcon className="visibility-icon" />
            </div>
          )}
        </>
      )
    );
  };

  const renderVideoTiles = () => {
    let otherUser = null;
    let name = null;
    if (callWaited) {
      otherUser = invites.filter((u) => u.useruuid !== currentUser.useruuid);
      otherUser = (otherUser || [])[0] || {};
      otherUser = allUserInfo[otherUser?.useruuid];
      name = getName(otherUser);
    }
    return callWaited ? (
      <UserVideoTile status={callStatus} name={name} user={otherUser} showNameplate={!isMini} />
    ) : !isAnyoneJoined && isEventCall ? (
      <div className="flex-center" style={{ width: '100%' }}>
        <span className="large-text-1 wait-message">{trans('WAITINGFOROTHERSTOJOIN')}</span>
      </div>
    ) : !isPresenting || isLocalUserSharing ? (
      <div className="video-grid" ref={ref}>
        {renderTiles()}
      </div>
    ) : (
      <div className="screen-share-container">
        <ContentShare className="screen-share-tile" nameplate="Content share" />
      </div>
    );
  };

  const renderVideoCallInfo = () => {
    const callDesc = calleeName ? trans('CALLWITH') + ' ' + calleeName : null;
    return (
      <div className="call-info-container">
        <div className="call-info">
          {isEventCall ? (
            <>
              <ScheduleIcon className="schedule-icon" />
              <Tooltip title={eventInfo?.event_name}>
                <label className="event-name">{eventInfo?.event_name}</label>
              </Tooltip>
            </>
          ) : (
            <>
              <CallIcon className="call-icon" />
              {callDesc && (
                <Tooltip title={callDesc}>
                  <label className="callee-info">{callDesc}</label>
                </Tooltip>
              )}
            </>
          )}
        </div>
        {duration && (
          <div className="timer-display">
            <label className="label">{trans('DURATION')}</label>
            <label className="value duration-value">{duration}</label>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (isCroppingEnabled) {
      setShowZoomModeBar(true);
    }
  }, [isCroppingEnabled]);

  const otherUser = useMemo(() => {
    return attendeeList.length === 1 ? attendeeList[0] : null;
  }, [attendeeList]);

  const hasPrincipalUser = useMemo(() => {
    if (attendeeList?.length !== 1) return false;
    return userHubsMapper[otherUser?.useruuid] ?? false;
  }, [attendeeList, userHubsMapper]);

  const principalDeviceUUID = useMemo(() => {
    if (!otherUser) return null;
    const principal = userHubsMapper[otherUser.useruuid];
    return principal?.deviceuuid ?? null;
  }, [otherUser, userHubsMapper]);

  useLayoutEffect(() => {
    if (selectedOption !== CallOptions.settings) {
      setSettingsSiderStyle(null);
      return;
    }

    const alignedStyle = getAlignedSettingsSiderStyle();
    if (alignedStyle) {
      setSettingsSiderStyle(alignedStyle);
    }
  }, [selectedOption, hasPrincipalUser, attendees.length, duration]);

  useEffect(() => {
    if (selectedOption !== CallOptions.settings) {
      return;
    }

    const updateSettingsSiderPosition = () => {
      const alignedStyle = getAlignedSettingsSiderStyle();
      if (alignedStyle) {
        setSettingsSiderStyle(alignedStyle);
      }
    };

    window.addEventListener('resize', updateSettingsSiderPosition);

    return () => {
      window.removeEventListener('resize', updateSettingsSiderPosition);
    };
  }, [selectedOption, hasPrincipalUser, attendees.length, duration]);

  const renderVideoCallActions = () => (
    <div className="video-call-actions-container-block">
      <div className="video-call-actions-container">
        <button
          type="button"
          className="video-call-action-block"
          aria-label={`${trans('CAMERA')} ${trans(isVideoEnabled ? 'OFF' : 'ON')}`}
          aria-pressed={isVideoEnabled}
          title={`${trans('CAMERA')} ${trans(isVideoEnabled ? 'OFF' : 'ON')}`}
          disabled={!isSetupDone}
          onClick={toggleVideo}
        >
          <div className="video-call-action-block-display">
            {isVideoEnabled ? <VideoOnIcon /> : <VideoOffIcon />}
            <span className="video-call-action-label">{trans('CAMERA')}</span>
          </div>
        </button>
        <button
          type="button"
          className="video-call-action-block"
          aria-label={`${trans('MIC')} ${trans(muted ? 'ON' : 'OFF')}`}
          aria-pressed={muted}
          title={`${trans('MIC')} ${trans(muted ? 'ON' : 'OFF')}`}
          disabled={!isSetupDone}
          onClick={toggleMute}
        >
          <div className="video-call-action-block-display">
            {muted ? <MicOffIcon /> : <MicOnIcon />}
            <span className="video-call-action-label">{trans('MIC')}</span>
          </div>
        </button>
      </div>
      <Divider className="video-call-divider" type="vertical" />
      <div className="video-call-content-block">
        <div
          className="video-call-action-block"
          onClick={() => handleOptionClick(CallOptions.invite)}
        >
          <div
            className={`video-call-action-block-display ${
              selectedOption === CallOptions.invite
                ? 'video-call-action-block-display-selected'
                : ''
            }`}
          >
            <div className="video-call-participant-container">
              <ContactIcon1 className="video-call-participant-icon" />
              <div className="video-call-count-container">
                <span className="video-call-count-value">{attendees?.length ?? 0}</span>
              </div>
            </div>
            <span className="video-call-action-label">{trans('PARTICIPANTS')}</span>
          </div>
        </div>
        {!isMini && (
          <div className="video-call-action-block" onClick={toggleContentShare}>
            <div
              className={`video-call-action-block-display ${isLocalUserSharing ? 'video-call-action-block-display-selected' : ''}`}
            >
              <ScreenShareOnIcon disabled={!isSetupDone} />
              {isLocalUserSharing ? (
                <span className="video-call-action-label">{trans('SHARING')}</span>
              ) : (
                <span className="video-call-action-label">{trans('SHARE')}</span>
              )}
            </div>
          </div>
        )}
        {!isMini && hasPrincipalUser && (
          <div
            className={`video-call-action-block ${attendees.length !== 2 ? 'disabled' : ''}`}
            disabled={!(attendees.length === 2 && hasPrincipalUser)}
            onClick={() => {
              if (attendees.length === 2 && hasPrincipalUser) {
                const nextState = !isCroppingEnabled;
                setIsCroppingEnabled(nextState);
                if (nextState) {
                  setCropControlKey((prev) => prev + 1);
                  setShowZoomModeBar(true);
                }
              }
            }}
          >
            <div
              className={`video-call-action-block-display ${
                isCroppingEnabled ? 'video-call-action-block-display-selected' : ''
              }`}
            >
              <ZoomIcon className="video-call-zoom-icon" />
              <span className="video-call-action-label">{trans('ZOOM')}</span>
            </div>
          </div>
        )}
        {!isMini && (
          <button
            type="button"
            className="video-call-action-block video-call-minimize-action"
            aria-label={trans('MINIMIZE_CALL')}
            title={trans('MINIMIZE_CALL')}
            onClick={onMinimize}
          >
            <div className="video-call-action-block-display minimise-icon-container">
              <img src={minimizeIconUrl} alt="" className="video-call-minimize-icon" />
              <span className="video-call-action-label">{trans('MINIMIZE_CALL')}</span>
            </div>
          </button>
        )}
        {!isMini && (
          <div
            className="video-call-action-block"
            ref={settingsActionRef}
            style={{
              cursor: isSetupDone ? 'pointer' : 'not-allowed',
              pointerEvents: isSetupDone ? 'auto' : 'none',
            }}
            onClick={() => {
              if (!isSetupDone) return;
              handleOptionClick(CallOptions.settings);
            }}
          >
            <div
              className={`video-call-action-block-display ${
                selectedOption === CallOptions.settings
                  ? 'video-call-action-block-display-selected'
                  : ''
              }`}
            >
              <SettingsIcon
                className={`settings-icon ${optionsClassname(CallOptions.settings)}`}
                disabled={!isSetupDone}
              />
              <span className="video-call-action-label">{trans('SETTINGS')}</span>
            </div>
          </div>
        )}
      </div>
      {!isMini && <Divider className="video-call-divider" type="vertical" />}
      {!isMini && (
        <div className="video-call-action-block">
          {duration && (
            <div className="video-call-action-block-display">
              <div className="video-call-duration-value-block">
                <span className="video-call-duration-value">{duration}</span>
              </div>
              <span className="video-call-action-label">{trans('DURATION')}</span>
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        className="video-call-end-call-block"
        aria-label={trans('ENDCALL_U')}
        title={trans('ENDCALL_U')}
        onClick={() =>
          callFailed
            ? onEndCall({ closeModal: true })
            : onEndCall({ leaveCall: true, endCall: true })
        }
      >
        <div className="video-call-action-block-display">
          {!callFailed && <EndCallIcon className="video-call-end-icon" />}
          <span className="video-call-end-call-label">
            {callFailed ? trans('videocall_ok') : trans('ENDCALL_U')}
          </span>
        </div>
      </button>
    </div>
  );

  useEffect(() => {
    // Pause transform device when camera turns off (preserve flags)
    if (!isVideoEnabled) {
      return;
    }
    // When camera turns on, re-apply transform effects only.
    // Do not auto-revert to none here because that persists "none" and can overwrite
    // the user's saved preference before restore logic runs.
    (async () => {
      if (lastEffectRef.current === 'blur') {
        await effectHandlers.handleBlurToggle?.(true);
      } else if (lastEffectRef.current === 'replacement' && selectedCustomBackground) {
        await effectHandlers.applyBackgroundReplacement?.(selectedCustomBackground);
      }
    })();
  }, [isVideoEnabled, selectedCustomBackground]);

  const renderOptionsSider = () => (
    <div
      ref={optionsSiderRef}
      className={`call-options-sider ${selectedOption === CallOptions.invite ? 'call-options-sider-participants' : 'call-options-sider-settings'}`}
      style={
        selectedOption === CallOptions.settings ? (settingsSiderStyle ?? undefined) : undefined
      }
    >
      <div
        className={`video-call-settings-container ${selectedOption === CallOptions.invite ? '' : 'video-call-settings-container-controls'}`}
      >
        <div
          className={`option-title-container ${selectedOption === CallOptions.invite ? '' : 'option-title-container-controls'}`}
        >
          <div className="video-call-option-title-icon-container">
            {selectedOption === CallOptions.invite ? (
              <ContactIcon className="video-call-participant-icon" />
            ) : (
              <SettingsIcon className="video-call-settings-icon" />
            )}
            <label className="option-title">{selectedOption}</label>
          </div>
          <CloseIcon
            className="close-icon clickable-item"
            onClick={() => setSelectedOption(null)}
          />
        </div>
        {selectedOption === CallOptions.invite ? (
          <InviteParticipants videoCallProp={videoCallProp} />
        ) : (
          <VideoCallSettings
            isSetupDone={isSetupDone}
            localChimeUser={localChimeUser}
            showSelfView={showSelfView}
            setShowSelfView={setShowSelfView}
            setIsBlurEnabled={setIsBlurEnabled}
            isBlurEnabled={isBlurEnabled}
            isReplacementEnabled={isReplacementEnabled}
            setIsReplacementEnabled={setIsReplacementEnabled}
            customBackgroundImage={customBackgroundImage}
            setSelectedCustomBackground={setSelectedCustomBackground}
            selectedCustomBackground={selectedCustomBackground}
            shouldShowProviderBackground={shouldShowProviderBackground}
            lastEffectRef={lastEffectRef}
            onProvideHandlers={setEffectHandlers}
            onApplyingStateChange={handleApplyingStateChange}
          />
        )}
      </div>
    </div>
  );

  const renderCallEnd = () => (
    <div className="call-end-container-1">
      <div className="call-end-container">
        <EndCallIcon className="white call-end-icon" />
        <div className="profile-pics-container">
          {invites.map((i) => {
            const user = allUserInfo[i.useruuid];
            const image = (user?.profilePics ?? {})[PhotoResolution.R64];
            return (
              <Tooltip key={i.useruuid} title={i.name}>
                <div>
                  {image ? (
                    <img src={image} className="user-profile-pic user-profile-image" alt="" />
                  ) : (
                    <ContactImage className="user-profile-pic" />
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
        <span className="header-1">{trans('CALLHASENDED')}</span>
        {isEventCall && eventInfo?.event_name && (
          <span className="body-text-1">{eventInfo?.event_name}</span>
        )}
        <span className="body-text-2">
          {callFormatDuration(moment(startTime, DateTimeFormat), moment(endTime, DateTimeFormat))}
        </span>
        <button
          type="button"
          className="video-call-end-container"
          onClick={() => onEndCall({ closeModal: true })}
        >
          <div className="video-call-end-block">
            <NewCloseIcon className="video-call-end-close-icon" />
            <div className="video-call-end-close-block">
              <span className="video-call-end-close-label">{trans('CLOSE')}</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`video-call-wrapper page-content-wrapper ${isMini ? 'video-call-mini' : ''} ${isMini && callEnded ? 'video-call-mini-ended' : ''} ${isMini && attendeeList.length === 1 ? 'video-call-mini-full-video' : ''}`}
    >
      {isMini && (
        <div className="video-call-mini-header">
          <span className="video-call-mini-title">
            {eventInfo?.event_name || calleeName || trans('VIDEOCALL')}
          </span>
          <div className="video-call-mini-toolbar">
            {!callEnded && (
              <>
                <span className="video-call-mini-duration" aria-live="off">
                  {formatMiniDuration(elapsedSeconds)}
                </span>
                <button
                  type="button"
                  className="video-call-mini-control"
                  aria-label={`${trans('CAMERA')} ${trans(isVideoEnabled ? 'OFF' : 'ON')}`}
                  aria-pressed={isVideoEnabled}
                  title={`${trans('CAMERA')} ${trans(isVideoEnabled ? 'OFF' : 'ON')}`}
                  disabled={!isSetupDone}
                  onClick={toggleVideo}
                >
                  {isVideoEnabled ? <VideoOnIcon /> : <VideoOffIcon />}
                </button>
                <button
                  type="button"
                  className="video-call-mini-control"
                  aria-label={`${trans('MIC')} ${trans(muted ? 'ON' : 'OFF')}`}
                  aria-pressed={muted}
                  title={`${trans('MIC')} ${trans(muted ? 'ON' : 'OFF')}`}
                  disabled={!isSetupDone}
                  onClick={toggleMute}
                >
                  {muted ? <MicOffIcon /> : <MicOnIcon />}
                </button>
                <button
                  type="button"
                  className="video-call-mini-control video-call-mini-end-call"
                  aria-label={trans('ENDCALL_U')}
                  title={trans('ENDCALL_U')}
                  onClick={() =>
                    callFailed
                      ? onEndCall({ closeModal: true })
                      : onEndCall({ leaveCall: true, endCall: true })
                  }
                >
                  <EndCallIcon className="video-call-end-icon" />
                </button>
              </>
            )}
            <button
              type="button"
              className="video-call-mini-control video-call-mini-maximize"
              aria-label={trans('RESTORE_CALL')}
              title={trans('RESTORE_CALL')}
              onClick={onExpand}
            >
              <img src={maximizeIconUrl} alt="" />
            </button>
          </div>
        </div>
      )}
      {callEnded ? (
        renderCallEnd()
      ) : (
        <Layout>
          <Layout.Content className="video-call-main-container">
            {isEventCall && (
              <div className="video-call-scheduled-call-container">
                <span className="video-call-scheduled-call-block">
                  <CallNewIcon className="video-call-scheduled-call-icon" />
                  <span className="video-call-scheduled-call-label">{eventInfo?.event_name}</span>
                </span>
              </div>
            )}
            <div className="video-tiles-container" ref={videoTilesContainerRef}>
              {renderVideoTiles()}
              {selectedOption !== CallOptions.settings && (
                <VideoCallSettings
                  isBootstrapOnly={true}
                  isSetupDone={isSetupDone}
                  localChimeUser={localChimeUser}
                  showSelfView={showSelfView}
                  setShowSelfView={setShowSelfView}
                  setIsBlurEnabled={setIsBlurEnabled}
                  isBlurEnabled={isBlurEnabled}
                  isReplacementEnabled={isReplacementEnabled}
                  setIsReplacementEnabled={setIsReplacementEnabled}
                  customBackgroundImage={customBackgroundImage}
                  setSelectedCustomBackground={setSelectedCustomBackground}
                  selectedCustomBackground={selectedCustomBackground}
                  shouldShowProviderBackground={shouldShowProviderBackground}
                  lastEffectRef={lastEffectRef}
                  onProvideHandlers={setEffectHandlers}
                  onApplyingStateChange={handleApplyingStateChange}
                />
              )}
              {selectedOption && !isMini && renderOptionsSider()}
            </div>
            <div className="video-footer-container">
              <div className="video-call-footer-block">
                <div className="local-video-container">{renderSelfTile()}</div>
                <div className="call-info-actions-container">
                  {/* {renderVideoCallInfo()} */}
                  {renderVideoCallActions()}
                </div>
              </div>
            </div>
          </Layout.Content>
        </Layout>
      )}
    </div>
  );
};

VideoCallComponent.propTypes = {
  header: PropTypes.array,
  isSetupDone: PropTypes.bool,
  callStatus: PropTypes.string,
  onEndCall: PropTypes.func,
  meetingInfo: PropTypes.object,
  participantsMuteStatus: PropTypes.object,
  videoCallProp: PropTypes.bool,
  customBackgroundImage: PropTypes.string,
  isMini: PropTypes.bool,
  onMinimize: PropTypes.func,
  onExpand: PropTypes.func,
};

export default VideoCallComponent;
